import {
  Activity,
  HeartHandshake,
  HeartPulse,
  Home,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

import type { DashboardResponse } from "@/api/dashboard";
import { AnimatedNumber } from "@/components/ui/AnimatedNumber";
import { Card } from "@/components/ui/card";

type Props = {
  dashboard: DashboardResponse;
};

export function ExecutiveSummary({ dashboard }: Props) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <SummaryCard
        accentClassName="from-emerald-500 via-teal-400 to-cyan-400"
        icon={<HeartPulse size={24} />}
        label="Church Health"
        value={dashboard.church_health}
        status={dashboard.church_health_status}
        iconClassName="bg-emerald-500/15 text-emerald-400"
        valueClassName={getHealthClass(
          dashboard.church_health,
        )}
        toLabel="Overall ministry health"
      />

      <SummaryCard
        accentClassName="from-indigo-500 via-violet-500 to-fuchsia-400"
        icon={<Activity size={24} />}
        label="Overall Church Attendance"
        value={dashboard.overall_attendance.total_present}
        status={`${dashboard.overall_attendance.unique_attendees} unique attendees`}
        iconClassName="bg-indigo-500/15 text-indigo-400"
        valueClassName="text-white"
        toLabel={`Last 30 days | ${dashboard.overall_attendance.average_attendance} average per occurrence`}
      />

      <SummaryCard
        accentClassName="from-amber-500 via-orange-400 to-yellow-300"
        icon={<Home size={24} />}
        label="Households in Watch"
        value={dashboard.households_at_risk}
        status={
          dashboard.unassessed_households > 0 || !dashboard.intelligence_run_id
            ? "Assessment incomplete"
            : dashboard.households_at_risk > 0
            ? "Needs attention"
            : "All clear"
        }
        iconClassName="bg-amber-500/15 text-amber-400"
        valueClassName={
          dashboard.unassessed_households > 0 || !dashboard.intelligence_run_id
            ? "text-slate-400"
            : dashboard.households_at_risk > 0
            ? "text-amber-400"
            : "text-emerald-400"
        }
        toLabel="Household intelligence"
      />

      <SummaryCard
        accentClassName="from-rose-500 via-pink-500 to-orange-400"
        icon={<HeartHandshake size={24} />}
        label="Open Follow-ups"
        value={dashboard.open_care_items}
        status={
          dashboard.open_care_items > 0
            ? "Needs review"
            : "All clear"
        }
        iconClassName="bg-rose-500/15 text-rose-400"
        valueClassName={
          dashboard.open_care_items > 0
            ? "text-rose-400"
            : "text-emerald-400"
        }
        toLabel="Care queue activity"
      />
    </div>
  );
}

function SummaryCard({
  accentClassName,
  icon,
  label,
  value,
  suffix,
  status,
  iconClassName,
  valueClassName,
  toLabel,
  trend,
}: {
  accentClassName: string;
  icon: React.ReactNode;
  label: string;
  value: number | null;
  suffix?: string;
  status: string;
  iconClassName: string;
  valueClassName: string;
  toLabel: string;
  trend?: number;
}) {
  return (
    <Card
      className="
        group relative cursor-default overflow-hidden p-5
        transition-all duration-300 ease-out
        hover:-translate-y-1 hover:scale-[1.01]
        hover:border-indigo-500/30
        hover:shadow-2xl hover:shadow-indigo-950/25
        active:translate-y-0 active:scale-[0.995]
      "
    >
      <div
        className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accentClassName}`}
      />

      <div className="absolute -right-10 -top-10 h-28 w-28 rounded-full bg-white/[0.025] blur-2xl transition group-hover:bg-white/[0.05]" />

      <div className="relative">
        <div className="flex items-start justify-between gap-4">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-xl transition duration-300 group-hover:scale-105 ${iconClassName}`}
          >
            {icon}
          </div>

          <span className="rounded-full border border-slate-800 bg-slate-950/70 px-3 py-1 text-xs font-semibold text-slate-400 transition group-hover:border-slate-700 group-hover:text-slate-300">
            {status}
          </span>
        </div>

        <p className="mt-5 text-sm font-semibold text-slate-300">
          {label}
        </p>

        <div className="mt-2 flex items-end gap-3">
          {value === null ? <span className="text-slate-400">Not yet assessed</span> : <AnimatedNumber
            value={value}
            suffix={suffix}
            duration={850}
            className={`text-4xl font-bold tracking-tight ${valueClassName}`}
          />}

          {trend !== undefined && trend !== 0 && (
            <span
              className={`mb-1 flex items-center gap-1 text-xs font-semibold ${
                trend > 0
                  ? "text-emerald-400"
                  : "text-rose-400"
              }`}
            >
              {trend > 0 ? (
                <TrendingUp size={14} />
              ) : (
                <TrendingDown size={14} />
              )}

              {Math.abs(trend).toFixed(1)}
            </span>
          )}
        </div>

        <p className="mt-3 text-xs text-slate-500 transition group-hover:text-slate-400">
          {toLabel}
        </p>
      </div>
    </Card>
  );
}

function getHealthClass(score: number | null) {
  if (score === null) return "text-slate-400";
  if (score >= 80) {
    return "text-emerald-400";
  }

  if (score >= 60) {
    return "text-amber-400";
  }

  return "text-rose-400";
}
